import type { MedicalCheckupOptionCatalogWithOption } from "@/server/models/medical-checkup/medical-checkup-option-catalog";
import type { MedicalCheckupPlanCatalogWithPlan } from "@/server/models/medical-checkup/medical-checkup-plan-catalog";
import { MemberEligibility } from "@/server/models/member/member-eligibility";
import { calculateFiscalYearEndAge } from "@/utils/fiscal-year";
import { stringUnionToArray } from "@/utils/string-union-to-array";
import { Database, Tables } from "@/utils/supabase/types";
import { z } from "zod";

export type MedicalCheckupSegment =
  Database["public"]["Enums"]["medical_checkup_segment"];

export type MedicalCheckupFrequency =
  Database["public"]["Enums"]["medical_checkup_frequency"];

export type MedicalCheckupTargetCondition =
  Database["public"]["Enums"]["medical_checkup_target_condition"];

/**
 * 健診機関の契約形態
 * - direct: 直契約（健保組合と健診機関が直契約している）
 * - toshinkyo: 東振協契約（東京都総合組合保健施設振興協会を通じた契約）
 * - none: 契約なし（契約外の健診機関）
 */
export type HealthCheckupFacilityContract =
  Database["public"]["Enums"]["health_checkup_facility_contract"];

export type HealthCheckupFacilityPrefecture =
  Database["public"]["Enums"]["health_checkup_facility_prefecture"];

export type MedicalCheckupPlan =
  Database["public"]["Enums"]["medical_checkup_plan"];
export const medicalCheckupPlanSchema = z.enum(
  stringUnionToArray<MedicalCheckupPlan>()(
    "basic_medical_checkup",
    "vcsu_comprehensive_medical_checkup",
    "specific_medical_checkup",
    "comprehensive_medical_checkup",
    "toshinkyo_a2",
    "toshinkyo_b_b1",
    "toshinkyo_d1",
  ),
);

export type MedicalCheckupOption =
  Database["public"]["Enums"]["medical_checkup_option"];

// 健診オプションの全コード集合（バリデーション・存在チェック用）。
// 型 MedicalCheckupOption（Supabase 生成の DB enum medical_checkup_option）を
// 源泉とし、順序には意味を持たせない。
// stringUnionToArray により全 enum 値を網羅していることが型で保証される。
export const medicalCheckupOptionCodes =
  stringUnionToArray<MedicalCheckupOption>()(
    "gastrointestinal_endoscopy_transoral",
    "gastrointestinal_endoscopy_transnasal",
    "barium_x_ray_examination",
    "abdominal_ultrasound_examination",
    "breast_mammography_and_ultrasound_examination",
    "breast_mammography",
    "breast_ultrasound",
    "cervical_cytology",
    "hpv_dna_test",
    "sputum_cytology",
    "low_dose_ct_scan",
    "colonoscopy",
    "psa",
  );

export const medicalCheckupOptionSchema = z
  .array(z.enum(medicalCheckupOptionCodes))
  .default([]);

// カタログ型はマスタ定義を plan 配下にネストするが、Rule はフラット形を維持する
// （消費側の互換のため）。マスタ由来の code / name は plan から引く。
export type MedicalCheckupPlanRule = Pick<
  MedicalCheckupPlanCatalogWithPlan,
  "targetAge" | "targetCondition"
> &
  Pick<MedicalCheckupPlanCatalogWithPlan["plan"], "code" | "name">;

// カタログ型はマスタ定義を option 配下にネストするが、Rule はフラット形を維持する
// （消費側の互換のため）。マスタ由来の code / name は option から引く。
export type MedicalCheckupOptionRule = Pick<
  MedicalCheckupOptionCatalogWithOption,
  "frequency" | "maxAmountOfSubsidy" | "segment" | "targetAge"
> &
  Pick<MedicalCheckupOptionCatalogWithOption["option"], "code" | "name">;

type FacilityItem = "Yes" | "No" | "Unknown";

export type FacilityDescription = {
  content: string | null;
  mainaHokensho: FacilityItem;
  multiLanguage: FacilityItem;
  barrierFree: FacilityItem;
};

export type HealthCheckupFacilityCatalog = {
  name: string;
  code: string;
  contract: HealthCheckupFacilityContract;
  plans: FacilityPlanCatalogEntry[];
  options: FacilityOptionCatalogEntry[];
  segment: MedicalCheckupSegment;
  archive_date: string | null;
};

// `health_checkup_facility_catalogs.plans` の JSON 由来の素のエントリ。
// JSON 上には label / description も存在し得るが、表示時には参照せず
// `medical_checkup_plans` 定義側を正として合成する (FacilityPlan を構築する)。
export type FacilityPlanCatalogEntry = {
  code: MedicalCheckupPlan;
  price: number;
};

// 健診コースの検査項目（カテゴリーごとの検査項目名の配列）
// `medical_checkup_plans.items`（Json型）由来。
export type InspectionCategory = {
  category: string;
  items: string[];
};

function isInspectionCategory(value: unknown): value is InspectionCategory {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  return (
    typeof obj.category === "string" &&
    Array.isArray(obj.items) &&
    obj.items.every((item) => typeof item === "string")
  );
}

/** DBのitemsカラム（Json型）をInspectionCategory[]にパースする */
export function parseInspectionItems(
  items: unknown,
): InspectionCategory[] | undefined {
  if (!Array.isArray(items) || items.length === 0) {
    return undefined;
  }

  if (!items.every(isInspectionCategory)) {
    return undefined;
  }

  return items;
}

// 表示用 (catalog エントリ + 定義から合成)
export type FacilityPlan = {
  code: MedicalCheckupPlan;
  description: string;
  label: string;
  price: number;
  // 健診コースの検査項目（マスタ medical_checkup_plans.items 由来）
  inspectionItems?: InspectionCategory[];
};

// `health_checkup_facility_catalogs.options` の JSON 由来の素のエントリ。
// JSON 上には label / description / prices[].plan_label も存在し得るが、
// 表示時には参照せず `medical_checkup_options` / `medical_checkup_plans`
// 定義側を正として合成する (FacilityOption を構築する)。
export type FacilityOptionCatalogEntry = {
  code: MedicalCheckupOption;
  price: number | null;
  prices: FacilityOptionPriceCatalogEntry[] | null;
};

// CSV インポート由来の prices 要素には `code`(オプションコード) は含まれない。
// オプションコードは親 entry (FacilityOptionCatalogEntry.code) を参照する。
type FacilityOptionPriceCatalogEntry = {
  plan_code: MedicalCheckupPlan;
  price: number;
};

// 表示用 (catalog エントリ + 定義から合成)
export type FacilityOption = {
  code: MedicalCheckupOption;
  description: string;
  label: string;
  price: number | null;
  prices: OptionPrices[] | null;
};

// オプションコース別価格。code は親 FacilityOption.code と冗長なため持たない。
type OptionPrices = {
  plan_code: MedicalCheckupPlan;
  plan_label: string;
  price: number;
};

export const MAX_MEMO_LENGTH = 240;

export type TargetAge =
  Database["public"]["Enums"]["medical_checkup_target_age"];

export const isWithinTargetAge = ({
  targetAge,
  age,
}: {
  targetAge: TargetAge;
  age: number;
}) => {
  switch (targetAge) {
    case "all":
      return true;
    case "under_30":
      return age < 30;
    case "over_20":
      return age >= 20;
    case "over_30":
      return age >= 30;
    case "over_30_under_35":
      return age >= 30 && age < 35;
    case "over_35":
      return age >= 35;
    case "over_40_under_75":
      return age >= 40 && age < 75;
    case "over_50":
      return age >= 50;
  }
};

export const isMatchingSegment = ({
  segment,
  member,
}: {
  segment: MedicalCheckupSegment;
  member: {
    sex: Database["public"]["Enums"]["sex"] | null;
  };
}) => {
  switch (segment) {
    case "general":
      return true;
    case "female":
    case "male":
      return member.sex === segment;
  }
};

export const isWithinFrequencySchedule = ({
  frequency,
  age,
}: {
  frequency: MedicalCheckupFrequency;
  age: number;
}) => {
  switch (frequency) {
    case "every_year":
      return true;
    case "once_every_2_years":
      return age % 2 === 0;
    case "once_every_5_years":
      return age % 5 === 0;
  }
};

export const isEligibleForTargetCondition = ({
  targetCondition,
  member,
}: {
  targetCondition: MedicalCheckupTargetCondition;
  member: {
    eligibility: MemberEligibility;
    insurance_number_id: {
      primary_insurance_number_id: string | null;
    } | null;
  };
}): boolean => {
  if (member.insurance_number_id === null) {
    return false;
  }
  switch (targetCondition) {
    case "primary":
      return (
        member.eligibility === "insured" &&
        member.insurance_number_id.primary_insurance_number_id === null
      );
    case "optional":
      return (
        member.eligibility === "optional_and_continued" &&
        member.insurance_number_id.primary_insurance_number_id === null
      );
    case "dependent":
      return member.insurance_number_id.primary_insurance_number_id !== null;
  }
};

// 補助金申請
// [直契約の健診機関]
// 申請はコース分もオプション分も不要。
// [東振協の健診機関]
// コース分は申請不要。オプションを受診した場合、オプション分は健診機関から
// 健保への請求が非対応のため、補助金申請が必要。結果の提出は不要。
// [それ以外]
// コース分もオプション分も補助金申請が必要。

/**
 * 健診コース code → コースに含まれるオプション code 配列 の map。
 * isPlanIncludedOption の判定ソースとして渡す。
 * マスタ駆動では fetchPlanIncludedOptionsMap() の戻り値を使用する。
 */
export type PlanIncludedOptionsMap = Partial<
  Record<MedicalCheckupPlan, MedicalCheckupOption[]>
>;

// 健診結果のcheckupCourse（CSVのコース名）からMedicalCheckupPlanへのマッピング
const CHECKUP_COURSE_TO_PLAN: Record<string, MedicalCheckupPlan> = {
  A2: "toshinkyo_a2",
  B1: "toshinkyo_b_b1",
  B: "toshinkyo_b_b1",
  D1: "toshinkyo_d1",
};

/**
 * 健診結果のcheckupCourse（CSVのコース名）からMedicalCheckupPlanに変換する
 */
export const checkupCourseToPlan = (
  checkupCourse: string | null,
): MedicalCheckupPlan | null => {
  if (!checkupCourse) {
    return null;
  }
  return CHECKUP_COURSE_TO_PLAN[checkupCourse] ?? null;
};

/**
 * 指定コースに含まれるオプションかどうかを判定する
 * コースに含まれるオプションは追加補助の対象外
 *
 * 判定ソースの map（コース code → 込みオプション code 配列）を第1引数で受け取る純関数。
 * map は fetchPlanIncludedOptionsMap()（マスタ medical_checkup_plans.included_option_codes 由来）で
 * 取得し、server なら直接、client なら props/Server Action 経由で渡す。
 * クライアントコンポーネントでも利用できるよう判定は同期で行う。
 */
export const isPlanIncludedOption = (
  includedOptionsMap: PlanIncludedOptionsMap,
  plan: MedicalCheckupPlan | null,
  optionCode: string,
): boolean => {
  if (!plan) {
    return false;
  }
  const includedOptions = includedOptionsMap[plan];
  if (!includedOptions) {
    return false;
  }
  return includedOptions.includes(optionCode as MedicalCheckupOption);
};

export const createNeedsSubsidyApplication = ({
  availableOptions,
  fiscalYear,
  planIncludedOptionsMap,
}: {
  availableOptions?: MedicalCheckupOptionRule[];
  fiscalYear?: number;
  // コースに含まれるオプション（追加補助の対象外）判定用のマスタ由来 map
  planIncludedOptionsMap: PlanIncludedOptionsMap;
}) => {
  return ({
    options,
    contract,
    member,
    plan,
  }: {
    options: Database["public"]["Enums"]["medical_checkup_option"][];
    contract: HealthCheckupFacilityContract | null;
    member: {
      date_of_birth: string;
      sex: Database["public"]["Enums"]["sex"] | null;
    };
    plan?: Database["public"]["Enums"]["medical_checkup_plan"] | null;
  }): boolean => {
    switch (contract) {
      case "direct":
        return false;
      case "toshinkyo": {
        if (fiscalYear === undefined) {
          return false;
        }
        const age = calculateFiscalYearEndAge(member.date_of_birth, fiscalYear);
        if (availableOptions === undefined) {
          return false;
        }
        const filteredOptions = availableOptions
          .filter((o) => isWithinTargetAge({ targetAge: o.targetAge, age }))
          .filter((o) => isMatchingSegment({ segment: o.segment, member }))
          .filter((o) =>
            isWithinFrequencySchedule({ frequency: o.frequency, age }),
          );
        // 補助金対象のoptionがあれば申請が必要
        const filteredOptionCodes = filteredOptions.map((o) => o.code);
        // コースに含まれるオプションを除外
        const planIncludedOptions = plan
          ? (planIncludedOptionsMap[plan] ?? [])
          : [];
        const optionsRequiringSubsidy = options.filter(
          (option) =>
            filteredOptionCodes.includes(option) &&
            !planIncludedOptions.includes(option),
        );
        if (optionsRequiringSubsidy.length > 0) {
          return true;
        }
        // それ以外は申請不要
        return false;
      }
      default:
        return true;
    }
  };
};

// 健診コースの自己負担金額を計算
export const calculatePlanSelfPayAmount = ({
  subsidyAmount,
  businessBurdenAmount,
  priceRange,
  relationshipToPrimary,
}: {
  subsidyAmount: number;
  businessBurdenAmount: number;
  priceRange: { min: number; max: number };
  relationshipToPrimary: string | null;
}): { min: number; max: number } => {
  // 被保険者以外 (任意継続被保険者、被扶養者)の場合は事業所負担はつねに 0 円
  const actualBusinessBurden =
    relationshipToPrimary === null ? businessBurdenAmount : 0;
  // #{ min - 事業所負担額 - 補助上限 } 〜 #{ max - 事業所負担額 - 補助上限 }
  const min = Math.max(
    0,
    priceRange.min - actualBusinessBurden - subsidyAmount,
  );
  const max = Math.max(
    0,
    priceRange.max - actualBusinessBurden - subsidyAmount,
  );
  return { min, max };
};

// 健診オプションの自己負担金額を計算
export const calculateOptionSelfPayAmount = ({
  subsidyAmount,
  priceRange,
}: {
  subsidyAmount: number;
  priceRange: { min: number; max: number };
}): { min: number; max: number } => {
  // #{ min - 補助上限} 〜 #{ max - 補助上限 }
  const min = Math.max(0, priceRange.min - subsidyAmount);
  const max = Math.max(0, priceRange.max - subsidyAmount);
  return { min, max };
};

// 健診コースの窓口支払額を計算
// 東振協契約の場合:
//   窓口支払額 = 価格 - 実際の健保補助額
//   実際の健保補助額 = min(健保補助額, 価格 - 事業所負担額)
//   ※被扶養者・任意継続被保険者は事業所負担額が0なので、実質的に「価格 - 健保補助額」と同じ
export const calculatePlanCounterPayAmount = ({
  contract,
  selfPayAmount,
  basePrice,
  subsidyAmount,
  businessBurdenAmount,
}: {
  contract: HealthCheckupFacilityContract;
  selfPayAmount: number;
  basePrice: number;
  subsidyAmount: number;
  /** 事業所負担額（被扶養者・任意継続被保険者は0） */
  businessBurdenAmount: number;
}): number => {
  // 直契約: 自己負担額と同じ
  if (contract === "direct") {
    return selfPayAmount;
  }
  // 東振協契約
  if (contract === "toshinkyo") {
    // 実際の健保補助額 = min(健保補助額, 価格 - 事業所負担額)
    const actualSubsidy = Math.min(
      subsidyAmount,
      Math.max(0, basePrice - businessBurdenAmount),
    );
    // 窓口支払額 = 価格 - 実際の健保補助額
    return Math.max(0, basePrice - actualSubsidy);
  }
  // 契約なし・不明: 価格
  return Math.max(0, basePrice);
};

// オプション検査の窓口支払額を計算
export const calculateOptionCounterPayAmount = ({
  contract,
  selfPayAmount,
  basePrice,
}: {
  contract: HealthCheckupFacilityContract;
  selfPayAmount: number;
  basePrice: number;
}): number => {
  // 直契約: 自己負担額と同じ
  if (contract === "direct") {
    return selfPayAmount;
  }
  // 東振協契約・契約なし・不明: ベース金額（補助額を差し引かない）
  return basePrice;
};

// 同意レコードから同意済みかどうかを判定する
// billing_procedure_agreement: 加入者が予約情報登録時に同意（2024年度のみ）
// submit_agreement: 加入者が予約情報登録時または問診回答時に同意（2024-2025年度）
// interview_answer_data_collaboration_agreement: 被保険者が問診回答時に同意（2026年度以降）
export const hasAgreement = (
  agreement: Pick<
    Tables<"medical_checkup_agreements">,
    | "billing_procedure_agreement"
    | "submit_agreement"
    | "interview_answer_data_collaboration_agreement"
  >,
): boolean => {
  return Boolean(
    agreement.billing_procedure_agreement ||
    agreement.submit_agreement ||
    agreement.interview_answer_data_collaboration_agreement,
  );
};

// 同意と問診必須回答がある場合、`回答済み` とし、それ以外は `未回答` とする
export const isInterviewAnswered = ({
  fiscalYear,
  hasInterviewAnswers,
  hasAgreements,
  healthCheckupFacilityCode,
  allInterviewIsRequired,
}: {
  fiscalYear: number;
  hasInterviewAnswers: boolean;
  hasAgreements: boolean;
  healthCheckupFacilityCode?: string | null;
  allInterviewIsRequired: boolean;
}): boolean => {
  if (fiscalYear === 2024) {
    // 2024年度は契約機関で受診する時は問診を任意とする
    const isOptional =
      healthCheckupFacilityCode !== undefined &&
      healthCheckupFacilityCode !== "NONE";
    return (
      // 問診回答がある場合、もしくは問診が任意の場合。同意は常に必要。
      (hasInterviewAnswers || isOptional) && hasAgreements
    );
  } else if (allInterviewIsRequired) {
    // 全問診項目が必須の時は、問診回答がある場合に回答済みとする。同意は常に必要。
    return hasInterviewAnswers && hasAgreements;
  } else {
    // TODO: 一部の問診項目が任意の場合は必須項目を全て回答済みか確認する必要があるが、2025年度は全項目が必須項目なので未実装
    throw new Error("Not implemented");
  }
};

// 性別に応じた健診機関セグメントを取得
export const getSegmentsForSex = (
  sex?: Database["public"]["Enums"]["sex"] | null,
): MedicalCheckupSegment[] => {
  const segments: MedicalCheckupSegment[] = ["general"];
  return sex ? [...segments, sex] : [...segments, "female", "male"];
};

// オプション検査の入力値が「true」または「TRUE」かどうかをチェックするヘルパー関数
export const isOptionTrue = (value: string | undefined | null): boolean => {
  return value === "true" || value === "TRUE";
};

/**
 * オプション検査の補助対象判定に必要な情報
 * NOTE: codeはPrismaの型とDBの型で差異があるため（ハイフンとアンダースコア）、string型を使用
 */
export type OptionSubsidyInput = {
  /** オプション検査コード */
  code: string;
  /** 対象年齢 */
  targetAge: TargetAge;
  /** 対象セグメント（性別） */
  segment: MedicalCheckupSegment;
  /** 受診頻度 */
  frequency: MedicalCheckupFrequency;
  /** 補助上限額 */
  maxAmountOfSubsidy: number;
};

/**
 * オプション検査の補助対象判定結果
 */
type OptionSubsidyResult = {
  /** 補助対象かどうか */
  isEligible: boolean;
  /** 補助額（補助対象外の場合は0） */
  subsidyAmount: number;
};

/**
 * カタログオプションから価格を取得する
 * price が null の場合、prices（プラン別価格）から受診コースに対応する価格を使用する
 * planCode が未指定、または一致する価格がない場合は null を返す
 */
const getCatalogOptionPrice = (
  catalogOption: FacilityOptionCatalogEntry | undefined | null,
  planCode?: MedicalCheckupPlan | null,
): number | null => {
  if (!catalogOption) {
    return null;
  }
  if (catalogOption.price != null) {
    return catalogOption.price;
  }
  if (!catalogOption.prices || catalogOption.prices.length === 0) {
    return null;
  }
  // 受診コースに対応する価格を取得
  if (planCode) {
    const matched = catalogOption.prices.find((p) => p.plan_code === planCode);
    if (matched) {
      return matched.price;
    }
  }
  // planCodeが不明、または一致する価格がない場合はnullを返す
  return null;
};

/**
 * オプションの補助額をカタログ情報を含めて計算する
 * カタログからオプション価格を取得し、補助対象判定と補助額計算を行う共通関数
 */
export const calculateOptionSubsidyWithCatalog = ({
  option,
  member,
  catalogOptions,
  planCode,
}: {
  option: OptionSubsidyInput;
  member: {
    age: number | null;
    sex: Database["public"]["Enums"]["sex"] | null;
  };
  catalogOptions: FacilityOptionCatalogEntry[] | undefined | null;
  planCode?: MedicalCheckupPlan | null;
}): {
  isEligible: boolean;
  subsidyAmount: number;
  catalogPrice: number | null;
} => {
  const catalogOption = catalogOptions?.find((o) => o.code === option.code);
  const catalogPrice = getCatalogOptionPrice(catalogOption, planCode);
  const { isEligible, subsidyAmount } = calculateOptionSubsidyEligibility({
    option,
    member,
    catalogPrice,
  });
  return { isEligible, subsidyAmount, catalogPrice };
};

/**
 * オプション検査が補助対象かどうかを判定し、補助額を計算する
 *
 * @param option - オプション検査情報
 * @param member - メンバー情報（年齢・性別）
 * @param catalogPrice - カタログに記載された検査価格（任意）
 * @returns 補助対象判定結果と補助額
 *
 * @knipignore
 */
export const calculateOptionSubsidyEligibility = ({
  option,
  member,
  catalogPrice,
}: {
  option: OptionSubsidyInput;
  member: {
    age: number | null;
    sex: Database["public"]["Enums"]["sex"] | null;
  };
  catalogPrice: number | null;
}): OptionSubsidyResult => {
  // 補助上限が0以下の場合は対象外
  if (option.maxAmountOfSubsidy <= 0) {
    return { isEligible: false, subsidyAmount: 0 };
  }

  // 年齢情報がなければ対象外
  if (member.age === null) {
    return { isEligible: false, subsidyAmount: 0 };
  }

  // 年齢条件のチェック
  if (!isWithinTargetAge({ targetAge: option.targetAge, age: member.age })) {
    return { isEligible: false, subsidyAmount: 0 };
  }

  // 性別条件のチェック
  if (!isMatchingSegment({ segment: option.segment, member })) {
    return { isEligible: false, subsidyAmount: 0 };
  }

  // 頻度条件のチェック
  if (
    !isWithinFrequencySchedule({ frequency: option.frequency, age: member.age })
  ) {
    return { isEligible: false, subsidyAmount: 0 };
  }

  // 補助額を計算: 補助上限と検査金額の小さい方
  const subsidyAmount =
    catalogPrice !== null
      ? Math.min(option.maxAmountOfSubsidy, catalogPrice)
      : 0;

  return { isEligible: true, subsidyAmount };
};

/**
 * オプション検査が補助対象かどうかを判定する（補助額計算なし）
 *
 * @param option - オプション検査情報（targetAge, segment, frequency）
 * @param member - メンバー情報（年齢・性別）
 * @returns 補助対象かどうか
 */
export const isOptionEligibleForSubsidy = ({
  option,
  member,
}: {
  option: Pick<OptionSubsidyInput, "targetAge" | "segment" | "frequency">;
  member: {
    age: number | null;
    sex: Database["public"]["Enums"]["sex"] | null;
  };
}): boolean => {
  // 年齢情報がなければ対象外
  if (member.age === null) {
    return false;
  }

  // 年齢条件のチェック
  if (!isWithinTargetAge({ targetAge: option.targetAge, age: member.age })) {
    return false;
  }

  // 性別条件のチェック
  if (!isMatchingSegment({ segment: option.segment, member })) {
    return false;
  }

  // 頻度条件のチェック
  if (
    !isWithinFrequencySchedule({ frequency: option.frequency, age: member.age })
  ) {
    return false;
  }

  return true;
};

/** @knipignore */
export const forTest = {
  getCatalogOptionPrice,
};
